import { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

const NEAR_BOTTOM_THRESHOLD = 150;
const LOAD_MORE_THRESHOLD = 80;
const EMPTY_LIST = [];

export const mergeMessages = (prev, incoming) => {
  const byId = new Map(prev.map((m) => [m.id, m]));
  incoming.forEach((m) => byId.set(m.id, m));
  return Array.from(byId.values()).sort(
    (a, b) => new Date(a.created_at) - new Date(b.created_at)
  );
};

export function useChatWindow({
  chat,
  queryKeyPrefix,
  fetchMessages,
  sendMessage,
  buildFormData,
  socketEvents,
  matchesChat,
  messagesByChat,
  setMessagesByChat,
}) {
  const queryClient = useQueryClient();
  const chatId = parseInt(chat?.chat_id);
  const messagesList = messagesByChat[chatId] || EMPTY_LIST;

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isGroupOwner, setIsGroupOwner] = useState(false);
  const [messageInput, setMessageInput] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const isNearBottomRef = useRef(true);
  const prevScrollHeightRef = useRef(0);
  const isLoadMoreFetchRef = useRef(false);
  const pendingScrollToBottomRef = useRef(false);

  useEffect(() => {
    if (!chatId) return;
    setPage(1);
    setHasMore(true);
    setIsLoadingMore(false);
    setShowScrollToBottom(false);
    setUnreadCount(0);
    isNearBottomRef.current = true;
    pendingScrollToBottomRef.current = true;
  }, [chatId]);

  const { data: messagesData, isLoading: messagesLoading } = useQuery({
    queryKey: [queryKeyPrefix, chatId, page],
    queryFn: () => fetchMessages(chatId, page),
    enabled: !!chatId,
    keepPreviousData: true,
  });

  useEffect(() => {
    if (!messagesData || !chatId) return;
    const incoming = messagesData?.message || messagesData?.data || [];
    setIsGroupOwner(messagesData?.is_group_owner ?? false);
    if (incoming.length === 0) {
      if (page > 1) setHasMore(false);
      setIsLoadingMore(false);
      return;
    }
    setMessagesByChat((prev) => ({
      ...prev,
      [chatId]: mergeMessages(prev[chatId] || [], incoming),
    }));
    setIsLoadingMore(false);
  }, [messagesData, chatId, page, setMessagesByChat]);

  const sendMutation = useMutation({
    mutationFn: (formData) => sendMessage(chatId, formData),
    onSuccess: (res) => {
      const sentMessage = res?.message || res?.data;
      if (sentMessage) {
        setMessagesByChat((prev) => ({
          ...prev,
          [chatId]: mergeMessages(prev[chatId] || [], [sentMessage]),
        }));
      }
      pendingScrollToBottomRef.current = true;
      setMessageInput("");
      setAttachments([]);
      queryClient.invalidateQueries({ queryKey: ["all_chats"] });
    },
  });

  // NOTE: no socket subscription here anymore — see MessagesPage.jsx.
  // This just reacts to messagesList changing (populated by the global
  // listener) to decide whether to auto-scroll or bump the unread badge
  // for the chat that's currently open.
  const lastMessageIdRef = useRef(null);
  useEffect(() => {
    if (!messagesList.length) return;
    const lastMsg = messagesList[messagesList.length - 1];
    const isNewTail =
      lastMessageIdRef.current !== null && lastMessageIdRef.current !== lastMsg.id;
    lastMessageIdRef.current = lastMsg.id;

    // Skip first population of a (re)mounted chat, and skip "load more"
    // (prepends older messages — tail id doesn't change).
    if (!isNewTail) return;

    if (isNearBottomRef.current) {
      pendingScrollToBottomRef.current = true;
    } else {
      setUnreadCount((c) => c + 1);
      setShowScrollToBottom(true);
    }
  }, [messagesList]);

  const scrollToBottom = useCallback((behavior = "smooth") => {
    messagesEndRef.current?.scrollIntoView({ behavior });
    setShowScrollToBottom(false);
    setUnreadCount(0);
    isNearBottomRef.current = true;
  }, []);

  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    const nearBottom = distanceFromBottom < NEAR_BOTTOM_THRESHOLD;
    isNearBottomRef.current = nearBottom;
    setShowScrollToBottom(!nearBottom);
    if (nearBottom) setUnreadCount(0);
    if (!isLoadingMore && hasMore && container.scrollTop < LOAD_MORE_THRESHOLD) {
      prevScrollHeightRef.current = container.scrollHeight;
      isLoadMoreFetchRef.current = true;
      setIsLoadingMore(true);
      setPage((p) => p + 1);
    }
  }, [isLoadingMore, hasMore]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  useLayoutEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    if (isLoadMoreFetchRef.current) {
      container.scrollTop = container.scrollHeight - prevScrollHeightRef.current;
      isLoadMoreFetchRef.current = false;
      return;
    }
    if (pendingScrollToBottomRef.current && messagesList.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
      pendingScrollToBottomRef.current = false;
      setShowScrollToBottom(false);
      setUnreadCount(0);
      isNearBottomRef.current = true;
    }
  }, [chatId, messagesList]);

  const handleSend = () => {
    if (!chatId || (!messageInput.trim() && attachments.length === 0)) return;
    sendMutation.mutate(buildFormData(chatId, messageInput.trim(), attachments));
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (attachments.length + files.length > 5) {
      alert("Maximum 5 attachments allowed");
      return;
    }
    setAttachments((prev) => [...prev, ...files]);
  };

  const removeAttachment = (index) =>
    setAttachments((prev) => prev.filter((_, i) => i !== index));

  const openImageModal = (url) => setSelectedImage(url);
  const closeImageModal = () => setSelectedImage(null);

  return {
    messagesList,
    messagesLoading,
    isLoadingMore,
    messagesContainerRef,
    messagesEndRef,
    showScrollToBottom,
    unreadCount,
    scrollToBottom,
    messageInput,
    setMessageInput,
    attachments,
    handleSend,
    handleFileSelect,
    removeAttachment,
    isSending: sendMutation.isPending,
    selectedImage,
    openImageModal,
    closeImageModal,
    isGroupOwner,
  };
}